import { useEffect, useRef } from 'react';
import { Group, Panel, type PanelImperativeHandle, Separator } from 'react-resizable-panels';

import clsx from 'clsx';

import { useSmartSearchPanelSize } from '~/hooks/smart-search';
import { useSmartSearchStore } from '~/store/common';

import FloatingPanel from './FloatingPanel';
import MiniBar from './MiniBar';
import SmartSearchContent from './SmartSearchContent';

import styles from './SmartSearchPanelGroup.module.scss';

const PANEL_COLLAPSED_SIZE = '0px';

const SmartSearchPanelGroup = ({ children }: { children: React.ReactNode }) => {
  const mode = useSmartSearchStore((s) => s.mode);
  const panelRef = useRef<PanelImperativeHandle>(null);
  const { defaultSize, minSize, maxSize, resizable } = useSmartSearchPanelSize();

  const isDocked = mode === 'docked';
  const isFloating = mode === 'floating';
  const isMinimized = mode === 'minimized';

  useEffect(() => {
    if (!panelRef.current) return;

    if (isDocked) {
      // minSize prop 반영 후 resize 적용 (react-resizable-panels 내부 제약 처리 대기)
      requestAnimationFrame(() => {
        panelRef.current?.resize(defaultSize);
      });
    } else {
      panelRef.current.resize(PANEL_COLLAPSED_SIZE);
    }
  }, [isDocked, defaultSize]);

  return (
    <Group orientation="horizontal" className={styles['panel-group']}>
      <Panel className={clsx(styles['main-panel'], isDocked && styles['main-panel-docked'])}>{children}</Panel>

      <Separator className={styles['separator']} disabled={!isDocked || !resizable} />

      <Panel
        panelRef={panelRef}
        className={styles['smart-search-panel']}
        defaultSize={PANEL_COLLAPSED_SIZE}
        minSize={isDocked ? minSize : PANEL_COLLAPSED_SIZE}
        maxSize={isDocked ? maxSize : PANEL_COLLAPSED_SIZE}
      >
        {isDocked && <SmartSearchContent />}
      </Panel>

      {isFloating && (
        <FloatingPanel>
          <SmartSearchContent />
        </FloatingPanel>
      )}
      {isMinimized && <MiniBar />}
    </Group>
  );
};

export default SmartSearchPanelGroup;
